import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import {
  detectRecurringTransactions,
  detectRecurringTransactionsTagLevel,
  getRecurringTransactions,
  getRecurringTransactionsTagLevel,
  getRecurringTransactionTransactions,
  getRecurringTransactionTransactionsTagLevel,
} from "../../../../docuclipper/api";
import { RecurringTransaction } from "../../../../docuclipper/DocuclipperTypes";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AgGridReact } from "ag-grid-react";
import { createAlert } from "src/redux/reducers/Alerts";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";
import { DateRangeFloatingFilter } from "../../../Docuclipper/AgGrid/DateRangeFloatingFilter";

type Props = {};

interface DrillDownTransaction {
  id?: number;
  documentId?: string;
  account?: string;
  date?: string;
  description?: string;
  amount?: number;
  [key: string]: unknown;
}

function sortByTotalDesc(a: RecurringTransaction, b: RecurringTransaction): number {
  const totalA = (a.canonicalAmount ?? 0) * (a.transactionCount ?? 0);
  const totalB = (b.canonicalAmount ?? 0) * (b.transactionCount ?? 0);
  return totalB - totalA;
}

export const RecurringTransactions: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const hasContext = !!(job?.id || (l1 && l2));

  const [list, setList] = useState<RecurringTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [drillDown, setDrillDown] = useState<{
    recurringTransaction: RecurringTransaction;
    transactions: DrillDownTransaction[];
  } | null>(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  const loadList = useCallback(() => {
    if (!hasContext) return;
    setLoading(true);
    const fn = job?.id
      ? getRecurringTransactions(job.id)
      : getRecurringTransactionsTagLevel(l1!, l2!);
    fn
      .then((r: { recurringTransactions?: RecurringTransaction[] }) => {
        const rows = (r.recurringTransactions ?? []).slice().sort(sortByTotalDesc);
        setList(rows);
      })
      .catch((err) => {
        dispatch(
          createAlert({
            message: err.message || "Error loading recurring transactions. Please try again.",
            timeout: 0,
            type: "error",
          })
        );
      })
      .finally(() => setLoading(false));
  }, [job?.id, l1, l2, hasContext, dispatch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const onDetect = () => {
    if (!hasContext) return;
    setDetecting(true);
    const fn = job?.id
      ? detectRecurringTransactions(job.id)
      : detectRecurringTransactionsTagLevel(l1!, l2!);
    fn
      .then((r: { recurringTransactions?: RecurringTransaction[] }) => {
        const rows = (r.recurringTransactions ?? []).slice().sort(sortByTotalDesc);
        setList(rows);
        dispatch(
          createAlert({
            message: "Recurring detection completed.",
            timeout: 3000,
            type: "success",
          })
        );
      })
      .catch((err) => {
        dispatch(
          createAlert({
            message: err.message || "Error running detection. Please try again.",
            timeout: 0,
            type: "error",
          })
        );
      })
      .finally(() => setDetecting(false));
  };

  const onRowClicked = (params: { data: RecurringTransaction }) => {
    const row = params.data;
    if (!hasContext || !row?.id) return;
    setDrillDownLoading(true);
    setDrillDown(null);
    const fn = job?.id
      ? getRecurringTransactionTransactions(job.id, row.id)
      : getRecurringTransactionTransactionsTagLevel(l1!, l2!, row.id);
    fn
      .then(
        (r: {
          recurringTransaction: RecurringTransaction;
          transactions: DrillDownTransaction[];
        }) => {
          setDrillDown({
            recurringTransaction: r.recurringTransaction,
            transactions: r.transactions ?? [],
          });
        }
      )
      .catch((err) => {
        dispatch(
          createAlert({
            message: err.message || "Error loading transactions. Please try again.",
            timeout: 0,
            type: "error",
          })
        );
      })
      .finally(() => setDrillDownLoading(false));
  };

  const [listColumnDefs] = useState([
    {
      field: "displayDescription",
      flex: 1,
      headerName: "Description",
      sortable: true,
      filter: "agTextColumnFilter",
    },
    {
      field: "canonicalAmount",
      width: 120,
      headerName: "Amount",
      sortable: true,
      filter: "agNumberColumnFilter",
      valueFormatter: (p: { value: number }) => p.value != null ? Number(p.value).toLocaleString() : "",
    },
    {
      colId: "totalAmount",
      headerName: "Total amount paid",
      width: 130,
      sortable: true,
      filter: "agNumberColumnFilter",
      valueGetter: (p) => {
        const a = p.data?.canonicalAmount;
        const n = p.data?.transactionCount;
        if (a == null || n == null) return null;
        return Number(a) * Number(n);
      },
      valueFormatter: (p: { value: number | null }) => p.value != null ? Number(p.value).toLocaleString() : "",
      filterValueGetter: (p) => {
        const a = p.data?.canonicalAmount;
        const n = p.data?.transactionCount;
        if (a == null || n == null) return null;
        return Number(a) * Number(n);
      },
    },
    {
      field: "interval",
      width: 110,
      headerName: "Interval",
      sortable: true,
      filter: "agSetColumnFilter",
      filterParams: { values: ["weekly", "biweekly", "monthly", "unknown"] },
    },
    {
      field: "transactionCount",
      width: 120,
      headerName: "Count",
      sortable: true,
      filter: "agNumberColumnFilter",
    },
    {
      field: "firstDate",
      width: 200,
      headerName: "First",
      sortable: true,
      filter: "agDateColumnFilter",
      filterParams: datefilterParamsYYYYMMDD,
      floatingFilterComponent: DateRangeFloatingFilter,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "lastDate",
      width: 200,
      headerName: "Last",
      sortable: true,
      filter: "agDateColumnFilter",
      filterParams: datefilterParamsYYYYMMDD,
      floatingFilterComponent: DateRangeFloatingFilter,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
  ]);

  const [drillColumnDefs] = useState([
    { field: "account", width: 140 },
    { field: "date", width: 110 },
    { field: "description", flex: 1 },
    { field: "amount", width: 100, valueFormatter: (p: { value: number }) => p.value != null ? Number(p.value).toLocaleString() : "" },
  ]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
    }),
    []
  );
  const listRef = useRef<AgGridReact>(null);

  const onBtnExport = useCallback(() => {
    if (listRef.current?.api) {
      listRef.current.api.exportDataAsCsv();
    }
  }, []);

  return (
    <div>
      <Row className="justify-content-end m-2">
        <Button variant="primary" onClick={onDetect} disabled={!hasContext || detecting}>
          <FontAwesomeIcon
            icon={detecting ? "spinner" : "search"}
            spin={detecting}
          />
          <span className="ml-1">{detecting ? "Detecting…" : "Detect Recurring"}</span>
        </Button>
      </Row>

      <div className="h3 mt-4">Recurring Transactions</div>

      <Row>
        <Col>
          <Button variant="link" onClick={onBtnExport}>
            <FontAwesomeIcon icon="download" />
            <span className="ml-1">Export as CSV</span>
          </Button>
        </Col>
      </Row>
      <Row>
        <div className="ag-theme-alpine" style={{ height: 500, width: "100%" }}>
          {loading ? (
            <div className="p-4">Loading…</div>
          ) : list.length === 0 ? (
            <div className="text-center text-muted p-5">
              No recurring transactions found. Click "Detect Recurring" to scan for repeating patterns across your transactions.
            </div>
          ) : (
            <AgGridReact
              ref={listRef as any}
              defaultColDef={defaultColDef}
              columnDefs={listColumnDefs}
              rowData={list}
              onRowClicked={onRowClicked}
              rowSelection="single"
              suppressRowClickSelection
              getRowId={(p) => String(p.data.id)}
            />
          )}
        </div>
      </Row>

      <Modal
        show={drillDown !== null || drillDownLoading}
        onHide={() => {
          if (!drillDownLoading) setDrillDown(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Recurring transaction details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {drillDownLoading && <div className="p-4">Loading…</div>}
          {!drillDownLoading && drillDown && (
            <>
              <Row className="mb-3">
                <Col>
                  <strong>{drillDown.recurringTransaction.displayDescription}</strong>
                  {" · "}
                  {drillDown.recurringTransaction.canonicalAmount != null &&
                    Number(drillDown.recurringTransaction.canonicalAmount).toLocaleString()}
                  {" · "}
                  {drillDown.recurringTransaction.interval}
                  {" · "}
                  {drillDown.recurringTransaction.transactionCount} transactions
                </Col>
              </Row>
              <div className="ag-theme-alpine" style={{ height: 400, width: "100%" }}>
                <AgGridReact
                  defaultColDef={defaultColDef}
                  columnDefs={drillColumnDefs}
                  rowData={drillDown.transactions}
                  getRowId={(p) => String(p.data?.id ?? p.rowIndex)}
                />
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RecurringTransactions;
